import { Key } from 'antd/es/table/interface';
import { ADMIN_ROUTES } from 'constants/routes';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, FormProps } from 'components';
import {
  useRoleListQuery,
  useTimeZoneListQuery,
  useUserByIdQuery,
  useUserCreateMutation,
  useUserUpdateMutation,
} from 'api';
import { getUpdatedPayload } from 'utils/helpers';
import dayjs from 'dayjs';
import { useNotificationApi } from 'hooks/useNotificationApi';
import { USER_TYPES } from '../../../../constants';

interface IUserForm {
  id?: string;
  email: string;
  firstName: string;
  lastName: string;
  languages: string[];
  countryId: string;
  city: string;
  timeZone: string;
  department: number;
  title: string;
  accessSameAsId: string;
  workingDays: string[];
  workingHours: any;
  workingHoursFrom: string;
  workingHoursTo: string;
  roleIds: string[];
  applicationAccess: (typeof USER_TYPES)[keyof typeof USER_TYPES][];
}

export const useAddEdit = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [targetKeys, setTargetKeys] = useState<Key[]>([]);
  const [dataToEdit, setDataToEdit] = useState<any | undefined>(undefined);
  const [disableSaveBtn, setDisableSaveBtn] = useState<boolean>(true);
  const [form] = Form.useForm();
  const { id } = useParams() as { id: string };
  const navigate = useNavigate();
  const notificationApi = useNotificationApi();

  const { data: userDetails = null } = useUserByIdQuery(
    { id },
    {
      skip: !id,
    }
  );
  const { data: timeZoneData = [] } = useTimeZoneListQuery();
  const {
    data: roleData = {},
    isLoading: roleIsLoading,
    isFetching,
  } = useRoleListQuery({
    page: currentPage,
    size: 10,
    search,
  });
  const [createUser, { isLoading: createIsLoading }] = useUserCreateMutation();
  const [updateUser, { isLoading: updateIsLoading }] = useUserUpdateMutation();
  const onChange = useCallback((targetKeys: Key[]) => {
    setTargetKeys(targetKeys);
  }, []);
  const onSearch = useCallback((value: string) => {
    setCurrentPage(1);
    setSearch(value);
  }, []);
  const onAccessUserChange = useCallback(
    (_id: string, value: Record<string, any>) => {
      const { roleIds = [] } = value;
      form.setFieldsValue({ roleIds: roleIds });
      setTargetKeys(roleIds.map((_role: any) => _role.id));
      setDataToEdit(roleIds);
    },
    [form]
  );
  const handlePageChange = useCallback((page: number) => setCurrentPage(page), []);
  const onValuesChange = useCallback(() => setDisableSaveBtn(false), []);

  const handleErrors = useCallback(
    (error: any) => {
      if (!error?.data?.success && error?.data?.data) {
        Object.values(error.data.data).forEach((message) => {
          notificationApi({
            type: 'error',
            message: message as string,
          });
        });
      } else {
        notificationApi({
          type: 'error',
          message: 'Something went wrong!',
        });
      }
    },
    [notificationApi]
  );

  const onCancel = useCallback(() => {
    form.resetFields();
    navigate(id ? `${ADMIN_ROUTES.users.view}/${id}/account` : ADMIN_ROUTES.users.list);
  }, [form, navigate, id]);

  const cancelWithNotification = useCallback(() => {
    form.resetFields();
    navigate(id ? `${ADMIN_ROUTES.users.view}/${id}/account` : ADMIN_ROUTES.users.list, {
      state: {
        notification: {
          type: 'success',
          message: `User was successfully ${userDetails ? 'updated' : 'created'}.`,
        },
      },
    });
  }, [form, navigate, id, userDetails]);

  const onFinish: FormProps<IUserForm>['onFinish'] = useCallback(
    async (values: IUserForm) => {
      if (values.workingHours) {
        values.workingHoursFrom = values.workingHours[0]?.format('HH:mm');
        values.workingHoursTo = values.workingHours[1]?.format('HH:mm');
        values.workingHours = undefined;
      }
      const payload = getUpdatedPayload(values, userDetails);
      payload.firstName = payload.firstName?.trim(); // Trim before submitting
      payload.lastName = payload.lastName?.trim(); // Trim before submitting
      payload.city = payload.city?.trim(); // Trim before submitting
      payload.title = payload.title?.trim(); // Trim before submitting
      if (id) {
        payload.id = id;
      }
      if (payload.roleIds) {
        payload.roleIds = payload.roleIds.map((role: any) => (role.id ? role.id : role));
      }

      try {
        const { data, error }: any = await (id ? updateUser : createUser)(payload);

        if (data?.success) {
          cancelWithNotification();
        } else {
          handleErrors(error);
        }
      } catch (err) {
        handleErrors(err);
      }
    },
    [createUser, updateUser, cancelWithNotification, handleErrors, userDetails, id]
  );

  useEffect(() => {
    if (id && userDetails) {
      const workingHours = userDetails.workingHoursFrom
        ? [dayjs(userDetails.workingHoursFrom, 'HH:mm'), dayjs(userDetails.workingHoursTo, 'HH:mm')]
        : [];
      form.setFieldsValue({ ...userDetails, workingHours });
      setTargetKeys(userDetails.roleIds?.map((_role: any) => _role?.id));
      setDataToEdit(userDetails.roleIds);
    }
  }, [id, userDetails, form]);

  return {
    form,
    onCancel,
    onFinish,
    isLoading: updateIsLoading || createIsLoading,
    disableSaveBtn,
    userDetails,
    dataToEdit,
    timeZoneData,
    roleData,
    isRoleLoading: roleIsLoading || isFetching,
    targetKeys,
    onChange,
    onSearch,
    handlePageChange,
    onValuesChange,
    onAccessUserChange,
  };
};
